import React from 'react';
import { Utensils, FileStack, Pill, LineChart } from 'lucide-react';
import { buildComplianceHistory } from '../../utils/complianceHistory';

/**
 * Compliance history: dietary, care plan versions, MAR log, assessment trends (Phase 1).
 */
const ComplianceHistoryPanel = ({ residentId }) => {
  const data = buildComplianceHistory(residentId);

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-2">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileStack className="w-5 h-5 text-indigo-600" />
          Compliance history &amp; audit trail
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Date-stamped changes for diet, care plan, medications, and assessments (demo data).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="mf-card p-5 rounded-xl border border-gray-200 bg-white">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Utensils className="w-4 h-4 text-amber-600" />
            Dietary &amp; IDDSI (effective dates)
          </h4>
          <ul className="space-y-2 text-sm">
            {data.dietaryHistory.map((row, i) => (
              <li key={i} className="border-l-4 border-amber-400 pl-3 py-1 bg-amber-50/50 rounded-r">
                <div className="font-medium text-gray-900">{row.effectiveDate}</div>
                <div className="text-gray-700">{row.summary}</div>
                {(row.iddsiFood || row.iddsiFluid) && (
                  <div className="text-xs text-gray-600 mt-0.5">
                    {[row.iddsiFood, row.iddsiFluid].filter(Boolean).join(' · ')}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="mf-card p-5 rounded-xl border border-gray-200 bg-white">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <FileStack className="w-4 h-4 text-indigo-600" />
            Care plan versions
          </h4>
          <ul className="space-y-2 text-sm">
            {data.carePlanVersions.map((row, i) => (
              <li key={i} className="border-l-4 border-indigo-400 pl-3 py-1 bg-indigo-50/50 rounded-r">
                <div className="flex justify-between gap-2">
                  <span className="font-mono text-xs font-semibold text-indigo-800">{row.version}</span>
                  <span className="text-xs text-gray-600">{row.effectiveDate}</span>
                </div>
                <div className="text-gray-700">{row.summary}</div>
                <div className="text-xs text-gray-500">{row.author}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="mf-card p-5 rounded-xl border border-gray-200 bg-white lg:col-span-2">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Pill className="w-4 h-4 text-green-600" />
            Medication administration (recent)
          </h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 pr-4">Date/time</th>
                  <th className="py-2 pr-4">Medication</th>
                  <th className="py-2 pr-4">Route</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2">Staff</th>
                </tr>
              </thead>
              <tbody>
                {data.medicationAdministrationLog.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 pr-4 whitespace-nowrap">{row.dateTime}</td>
                    <td className="py-2 pr-4">{row.medication}</td>
                    <td className="py-2 pr-4">{row.route}</td>
                    <td className="py-2 pr-4">{row.status}</td>
                    <td className="py-2">{row.staff}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mf-card p-5 rounded-xl border border-gray-200 bg-white lg:col-span-2">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <LineChart className="w-4 h-4 text-purple-600" />
            Assessment trends (selected tools)
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.assessmentTrends.map((row, i) => (
              <div
                key={i}
                className="flex justify-between items-center p-3 rounded-lg bg-purple-50 border border-purple-100"
              >
                <div>
                  <div className="text-xs text-purple-700 font-medium">{row.date}</div>
                  <div className="font-medium text-gray-900">{row.assessment}</div>
                  <div className="text-xs text-gray-600">{row.outcome}</div>
                </div>
                <div className="text-lg font-bold text-purple-800">{row.score}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceHistoryPanel;
